import React from 'react';
import { useTranslation } from 'react-i18next';
import { IconButton } from '@engyalo/design-system';
import { OutgoingMessageChatMessageKind } from '@engyalo/salesdesk-chat-socket-ts-client';
import { MessageType } from 'components/Messages';
import { ReplyPreviewContainer, AccentBar, PreviewContent, SenderName, PreviewText } from './style';

interface ReplyPreviewProps {
  /** The message being replied to. */
  message: MessageType;
  /** Called when the agent dismisses the reply (clears the selection). */
  onDismiss: () => void;
}

const MEDIA_PREVIEW_I18N_KEYS: Partial<Record<OutgoingMessageChatMessageKind, string>> = {
  [OutgoingMessageChatMessageKind.Image]: 'image',
  [OutgoingMessageChatMessageKind.Video]: 'videoMessage',
  [OutgoingMessageChatMessageKind.Audio]: 'audioMessage',
  [OutgoingMessageChatMessageKind.Document]: 'documentMessage',
};

const MEDIA_PREVIEW_ICONS: Partial<Record<OutgoingMessageChatMessageKind, string>> = {
  [OutgoingMessageChatMessageKind.Image]: '\u{1F4F7}',
  [OutgoingMessageChatMessageKind.Video]: '\u{1F3A5}',
  [OutgoingMessageChatMessageKind.Audio]: '\u{1F3B5}',
  [OutgoingMessageChatMessageKind.Document]: '\u{1F4C4}',
};

/**
 * Displays a condensed preview of the quoted message above the chat input bar.
 * Shows sender name and a single-line summary (text body or media-type label).
 */
const ReplyPreview: React.FC<ReplyPreviewProps> = ({ message, onDismiss }) => {
  const { t } = useTranslation();
  const senderName = message.chat?.name || message.chat?.username || '';

  const getPreviewText = (): string => {
    const kind = message.chat?.kind;
    const i18nKey = kind && MEDIA_PREVIEW_I18N_KEYS[kind];
    if (i18nKey) {
      return `${MEDIA_PREVIEW_ICONS[kind as OutgoingMessageChatMessageKind]} ${t(i18nKey)}`;
    }
    return message.chat?.text?.body || t('message');
  };

  return (
    <ReplyPreviewContainer data-testid="replyPreview">
      <AccentBar />
      <PreviewContent>
        <SenderName data-testid="replyPreviewSender" noWrap>
          {senderName}
        </SenderName>
        <PreviewText data-testid="replyPreviewText" noWrap>
          {getPreviewText()}
        </PreviewText>
      </PreviewContent>
      <IconButton
        data-testid="replyPreviewDismiss"
        onClick={onDismiss}
        variant="text"
        color="neutral2"
        size="small"
        aria-label={t('cancel')}
      >
        <i className="fa-regular fa-xmark" />
      </IconButton>
    </ReplyPreviewContainer>
  );
};

export default ReplyPreview;
